/**
 * Modal-surface scroll-lock context — the seam that keeps every popover-based
 * dropdown mouse-wheel-scrollable inside a dialog.
 *
 * A MODAL Radix Dialog (and therefore `Sheet` and `AlertDialog`, which are the
 * same primitive) installs `react-remove-scroll`. That lock adds a
 * document-level, non-passive `wheel` listener and `preventDefault()`s every
 * event whose target is neither inside the lock's own node nor inside one of the
 * refs it was handed as `shards` — and Radix shards exactly one node, the
 * dialog's own content.
 *
 * Every dropdown in this design system — `Select`, `EntityPickerBase`,
 * `DatePicker`, the list filters — renders its panel through `PopoverContent`,
 * which PORTALS to `<body>`. So the panel is outside the lock and outside the
 * shard, and its option list scrolled by keyboard and by scrollbar drag but not
 * by wheel: every option below the fold was unreachable without typing a search.
 * That is what the Assign Role dialog's 24-role list looked like (WI 4e837486),
 * and it was never specific to that dialog — the same lock sits under every
 * dialog in the app.
 *
 * The fix, in `PopoverContent`: while the panel is inside an open modal surface,
 * give it a `react-remove-scroll` lock of its OWN, with that surface's content
 * node as a shard. The new lock is pushed last onto the library's shared lock
 * stack, so the dialog's listener stands down; the new lock then knows about
 * both the panel (its own node) and the dialog body (its shard), and blocks only
 * the page behind — which is what the dialog's lock was there to do.
 *
 * **Why not Radix's `modal` prop on the Popover, which mounts the same lock in
 * one line.** It was tried first and reverted. `PopoverContentModal` also sets
 * `disableOutsidePointerEvents` and calls `hideOthers()`, so for as long as a
 * dropdown is open the dialog behind it is `pointer-events: none` AND
 * `aria-hidden`: a sighted user needs two clicks to reach Cancel, and a screen
 * reader cannot see the dialog at all. Measured in the browser, and caught by
 * `UserRolesSection.assignDialog.test.tsx` failing to find the dialog's Cancel
 * button. Paying that on every dropdown in every dialog is a worse bug than the
 * one being fixed, so the lock is mounted directly instead of through `modal`.
 *
 * A popover on a PAGE must take no lock at all: there is nothing to defeat, and
 * a lock there would stop the page behind from scrolling. So the modality has to
 * follow the surface, which is what these two contexts carry:
 *
 * - `ModalSurfaceRoot` publishes the modality a `Dialog`/`Sheet` root was given.
 *   A non-modal Radix dialog renders no overlay and therefore installs no lock
 *   (see `DialogOverlay` in `@radix-ui/react-dialog`), so a popover inside one
 *   must not take a lock either.
 * - `ModalSurfaceContent` republishes the surface's content NODE from inside the
 *   content itself, so a popover rendered as a SIBLING of `DialogContent` — next
 *   to a `DialogTrigger`, while the dialog is shut — is not caught by it.
 *
 * `PopoverContent` reads the second one. Nothing else should need either.
 */
import * as React from 'react';

/** Modality declared by a Dialog/Sheet root, read by its own Content. */
const ModalSurfaceModalityContext = React.createContext(true);

/**
 * The open modal surface's content node, or `null` when this subtree is not
 * inside one (a plain page, or a non-modal sheet).
 */
const ModalSurfaceContentContext =
  React.createContext<React.RefObject<HTMLElement | null> | null>(null);

/**
 * Publishes a modal surface ROOT's modality. `AlertDialog` has no `modal` prop —
 * it is always modal — but still wraps, so a non-modal `Sheet` around it cannot
 * leak `false` in.
 */
export function ModalSurfaceRoot({
  modal,
  children,
}: {
  modal: boolean;
  children: React.ReactNode;
}) {
  return (
    <ModalSurfaceModalityContext.Provider value={modal}>
      {children}
    </ModalSurfaceModalityContext.Provider>
  );
}

/**
 * Merges a Content component's forwarded ref with a local one, so the same node
 * can be handed to {@link ModalSurfaceContent} as the shard.
 */
export function useModalSurfaceContentRef<T extends HTMLElement>(
  forwardedRef: React.Ref<T>,
) {
  const contentRef = React.useRef<T | null>(null);
  const setContentRef = React.useCallback(
    (node: T | null) => {
      contentRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) {
        (forwardedRef as React.MutableRefObject<T | null>).current = node;
      }
    },
    [forwardedRef],
  );
  return { contentRef, setContentRef };
}

/** Wraps a modal surface's CONTENT, arming the seam for everything inside it. */
export function ModalSurfaceContent({
  contentRef,
  hasOverlay = true,
  children,
}: {
  contentRef: React.RefObject<HTMLElement | null>;
  /**
   * Whether this surface rendered its OVERLAY, which is the element Radix hangs
   * the scroll lock on — not the content. So a `<Sheet modal hideOverlay>` is
   * modal and yet installs no lock, and a popover inside one that took a lock of
   * its own would newly stop the page behind from scrolling. No such caller
   * exists today (every `hideOverlay` site is also `modal={false}`), which is
   * exactly why it is worth spelling before one does.
   */
  hasOverlay?: boolean;
  children: React.ReactNode;
}) {
  const modal = React.useContext(ModalSurfaceModalityContext);
  return (
    <ModalSurfaceContentContext.Provider value={modal && hasOverlay ? contentRef : null}>
      {children}
    </ModalSurfaceContentContext.Provider>
  );
}

/**
 * The content node of the open modal surface around this subtree, or `null` when
 * there is none — i.e. when no `react-remove-scroll` lock owns the document and
 * a portaled popover needs no lock of its own.
 */
export function useModalSurfaceContent(): React.RefObject<HTMLElement | null> | null {
  return React.useContext(ModalSurfaceContentContext);
}
